/**
 * WizardSubHeader — D5-A sticky relationship line + grant summary.
 * STORY-1305 AC#7 (Sprint 17 W1) + review patches 2026-05-13:
 * - D3: emoji → lucide-react icons (CLAUDE.md emoji policy compliance).
 * - P4: PERMANENT grantee name via usePartner(id) instead of raw UUID.
 * - P7: payer via usePartner(id) — not bounded by usePartners() page size.
 * - P8: trim-and-fallback for empty granteeData.name.
 * - P11: defensive null checks on partner-lookup hooks.
 * - P12: sticky top via CSS var (--wizard-header-height) instead of magic 60px.
 * - D1=C: pending-one-time sentinel renders the captured granteeData name.
 */

import { useActiveGrantsForGrantor } from '@/hooks/use-auth-grants';
import { usePartner } from '@/hooks/use-partners';
import { useTranslation } from '@kgc/i18n';
import { Building2, PackageCheck, User } from 'lucide-react';
import { useMemo } from 'react';
import { PENDING_ONE_TIME_SENTINEL, useWizardStore } from '../wizard-store';

function trimOrFallback(value: string | undefined | null, fallback: string | null): string | null {
  if (value?.trim()) return value.trim();
  return fallback;
}

export function WizardSubHeader() {
  const { t } = useTranslation();
  const { partner, costBearerId, grantId, pendingOneTimeGrant } = useWizardStore();
  const { grants } = useActiveGrantsForGrantor(partner?.id ?? null);

  // P7: byId lookup — bypasses the 20-record page-size ceiling of usePartners().
  const showPayer = !!costBearerId && !!partner && costBearerId !== partner.id;
  const { partner: payerPartner } = usePartner(showPayer ? costBearerId : undefined);

  const isPendingOneTime = grantId === PENDING_ONE_TIME_SENTINEL;

  const realGrant = useMemo(() => {
    if (!grantId || isPendingOneTime) return null;
    return grants.find(g => g.id === grantId) ?? null;
  }, [grantId, grants, isPendingOneTime]);

  // P4: PERMANENT grantee → partner display name lookup.
  const granteeLookupId = realGrant?.granteePartnerId ?? undefined;
  const { partner: granteePartner } = usePartner(granteeLookupId);

  const granteeDisplayName = useMemo<string | null>(() => {
    if (isPendingOneTime && pendingOneTimeGrant) {
      return trimOrFallback(pendingOneTimeGrant.name, null);
    }
    if (!realGrant) return null;
    return (
      trimOrFallback(realGrant.granteeData?.name, null) ??
      trimOrFallback(granteePartner?.name, realGrant.id.slice(0, 8))
    );
  }, [isPendingOneTime, pendingOneTimeGrant, realGrant, granteePartner]);

  if (!partner) return null;

  return (
    <div
      className="sticky top-[var(--wizard-header-height,60px)] z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-[#2d7d8a] px-4 py-2"
      data-testid="wizard-sub-header"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 text-xs text-gray-600 dark:text-[#ccfbf1]">
        <span
          className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-primary"
          data-testid="sub-header-connection-label"
        >
          {t('rental.checkout.subheader.connectionLabel')}
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="sub-header-renter"
          aria-label={t('rental.checkout.subheader.renter.label')}
        >
          <User className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="font-semibold">{partner.name}</span>
          <span>{t('rental.checkout.subheader.renter.label')}</span>
        </span>

        {showPayer && payerPartner && (
          <>
            <span className="hidden sm:inline text-muted-foreground" aria-hidden="true">
              ·
            </span>
            <span
              className="inline-flex items-center gap-1.5"
              data-testid="sub-header-payer"
              aria-label={t('rental.checkout.subheader.payer.label')}
            >
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-semibold">{payerPartner.name}</span>
              <span>{t('rental.checkout.subheader.payer.label')}</span>
            </span>
          </>
        )}

        {granteeDisplayName && (
          <>
            <span className="hidden sm:inline text-muted-foreground" aria-hidden="true">
              ·
            </span>
            <span
              className="inline-flex items-center gap-1.5"
              data-testid="sub-header-grantee"
              aria-label={t('rental.checkout.subheader.grantee.label')}
            >
              <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-semibold">{granteeDisplayName}</span>
              <span>{t('rental.checkout.subheader.grantee.label')}</span>
              {realGrant && (
                <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                  {realGrant.id.slice(0, 8)}
                </span>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
